/**
 * OperatingModesEditor (Phase-1 S1 / W4) — the edit surface for an equipment's
 * operating-mode model AND its per-parameter activation: modes (add / rename /
 * remove, set current), transitions (add / remove, edit from / to / event + the
 * trigger the S4 predicate runtime fires on), and which modes each measurement
 * parameter is active in (active_modes). Everything rides `data.entity` through
 * the existing upsert, so a plant authors it in the portal (active_modes promoted
 * from simulator-catalog-only to platform storage).
 *
 * The whole model is one combined draft (om + params) so a mode rename or removal
 * cascades atomically into the transitions AND the parameter activation. Save is
 * blocked until the model validates (the server's FSM rules, an editor-side
 * trigger guard, and the active-mode rule that every activation names a declared
 * mode). The predicate dropdown offers only the predicates the streaming runtime
 * evaluates, so it never presents one that would do nothing.
 */

import { useState } from "react";
import { Plus, Save, Workflow, X } from "lucide-react";

import type { EntityDef, ParameterDef } from "@foundry/foundry-plant";
import { STREAMING_PREDICATES, type OperatingModes } from "@/lib/plant/operating-modes";
import {
  addMode,
  addTransition,
  emptyOperatingModes,
  removeModeAt,
  removeTransitionAt,
  renameModeAt,
  setCurrentMode,
  setTriggerPredicateAt,
  toggleFromAt,
  updateTransitionAt,
  updateTriggerAt,
  validateOperatingModes,
  validateTriggers,
} from "@/lib/plant/operating-modes-edit";
import {
  dropModeFromParams,
  renameModeInParams,
  toggleParamModeAt,
  validateParamActiveModes,
} from "@/lib/plant/param-activation-edit";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";

const SELECT_CLASS =
  "rounded-md border border-border bg-surface px-2 py-1 font-mono text-xs text-on-surface";

/** The whole editable model: the FSM plus the parameters (for active_modes). */
interface EditDraft {
  om: OperatingModes;
  params: ParameterDef[];
}

/** Parse a numeric-input string to a number, or undefined when blank. */
function numOrUndef(v: string): number | undefined {
  return v.trim() === "" ? undefined : Number(v);
}

/** One editable transition: from toggles, to select, event input, trigger fields. */
function TransitionEditorRow({
  om,
  index,
  onChange,
}: {
  om: OperatingModes;
  index: number;
  onChange: (next: OperatingModes) => void;
}) {
  const t = om.transitions[index];
  const pred = t.trigger?.predicate ?? "";
  const isAnalog = pred === "analog_cross_above" || pred === "analog_cross_below";
  const isCoilOrCount =
    pred === "coil_rising" || pred === "coil_falling" || pred === "count_pulse";

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-muted/40 px-2.5 py-2">
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-on-surface-muted">from</span>
        {om.modes.map((m) => {
          const on = (t.from ?? []).includes(m);
          return (
            <Button
              key={m}
              size="sm"
              variant={on ? "secondary" : "ghost"}
              aria-pressed={on}
              className="h-6 px-1.5 font-mono text-[11px]"
              onClick={() => onChange(toggleFromAt(om, index, m))}
            >
              {m}
            </Button>
          );
        })}
        {(t.from?.length ?? 0) === 0 && (
          <span className="text-[11px] text-on-surface-muted">(any)</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-on-surface-muted">to</span>
        <select
          aria-label={`Transition ${index + 1} target mode`}
          value={t.to}
          className={SELECT_CLASS}
          onChange={(e) => onChange(updateTransitionAt(om, index, { to: e.target.value }))}
        >
          {om.modes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <span className="text-on-surface-muted">on</span>
        <Input
          value={t.on_event}
          placeholder="event name"
          aria-label={`Transition ${index + 1} event`}
          className="h-7 w-40 font-mono text-xs"
          onChange={(e) => onChange(updateTransitionAt(om, index, { on_event: e.target.value }))}
        />
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Remove transition ${index + 1}`}
          className="ml-auto"
          onClick={() => onChange(removeTransitionAt(om, index))}
        >
          <X className="size-3.5" />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-on-surface-muted">trigger</span>
        <select
          aria-label={`Transition ${index + 1} predicate`}
          value={pred}
          className={SELECT_CLASS}
          onChange={(e) =>
            onChange(setTriggerPredicateAt(om, index, e.target.value || undefined))
          }
        >
          <option value="">manual / derived</option>
          {STREAMING_PREDICATES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        {pred && (
          <Input
            value={t.trigger?.tag_hint ?? ""}
            placeholder="tag hint"
            aria-label={`Transition ${index + 1} tag hint`}
            className="h-7 w-48 font-mono text-xs"
            onChange={(e) =>
              onChange(updateTriggerAt(om, index, { tag_hint: e.target.value || undefined }))
            }
          />
        )}
        {isAnalog && (
          <>
            <Input
              type="number"
              value={t.trigger?.level ?? ""}
              placeholder="level"
              aria-label={`Transition ${index + 1} level`}
              className="h-7 w-24 font-mono text-xs"
              onChange={(e) =>
                onChange(updateTriggerAt(om, index, { level: numOrUndef(e.target.value) }))
              }
            />
            <Input
              type="number"
              value={t.trigger?.deadband ?? ""}
              placeholder="deadband"
              aria-label={`Transition ${index + 1} deadband`}
              className="h-7 w-24 font-mono text-xs"
              onChange={(e) =>
                onChange(updateTriggerAt(om, index, { deadband: numOrUndef(e.target.value) }))
              }
            />
            <Input
              type="number"
              value={t.trigger?.min_dwell_ms ?? ""}
              placeholder="dwell ms"
              aria-label={`Transition ${index + 1} minimum dwell ms`}
              className="h-7 w-24 font-mono text-xs"
              onChange={(e) =>
                onChange(updateTriggerAt(om, index, { min_dwell_ms: numOrUndef(e.target.value) }))
              }
            />
          </>
        )}
        {isCoilOrCount && (
          <Input
            type="number"
            value={t.trigger?.min_gap_ms ?? ""}
            placeholder="gap ms"
            aria-label={`Transition ${index + 1} minimum gap ms`}
            className="h-7 w-24 font-mono text-xs"
            onChange={(e) =>
              onChange(updateTriggerAt(om, index, { min_gap_ms: numOrUndef(e.target.value) }))
            }
          />
        )}
      </div>
    </div>
  );
}

/** One parameter's activation: its name + a mode toggle per declared mode. */
function ParamActivationRow({
  param,
  index,
  modes,
  onToggle,
}: {
  param: ParameterDef;
  index: number;
  modes: string[];
  onToggle: (index: number, mode: string) => void;
}) {
  const active = param.active_modes ?? [];
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="min-w-36 font-mono text-on-surface">{param.name}</span>
      {modes.map((m) => {
        const on = active.includes(m);
        return (
          <Button
            key={m}
            size="sm"
            variant={on ? "secondary" : "ghost"}
            aria-pressed={on}
            className="h-6 px-1.5 font-mono text-[11px]"
            onClick={() => onToggle(index, m)}
          >
            {m}
          </Button>
        );
      })}
      {active.length === 0 && (
        <span className="text-[11px] text-on-surface-muted">(all modes)</span>
      )}
    </div>
  );
}

export interface OperatingModesEditorProps {
  entity: EntityDef;
  onSave: (next: EntityDef) => void;
  onDone: () => void;
}

/** The full operating-mode + parameter-activation edit form; validates, saves via upsert. */
export function OperatingModesEditor({ entity, onSave, onDone }: OperatingModesEditorProps) {
  const [draft, setDraft] = useState<EditDraft>(() => ({
    om: entity.operating_modes ?? emptyOperatingModes(),
    params: entity.parameters ?? [],
  }));
  const [newMode, setNewMode] = useState("");

  const errors = [
    ...validateOperatingModes(draft.om),
    ...validateTriggers(draft.om),
    ...validateParamActiveModes(draft.params, draft.om.modes),
  ];

  // A mode rename/removal cascades atomically into the transitions AND the parameter activation.
  const renameMode = (i: number, next: string) =>
    setDraft((d) => ({
      om: renameModeAt(d.om, i, next),
      params: renameModeInParams(d.params, d.om.modes[i], next),
    }));
  const removeMode = (i: number) =>
    setDraft((d) => ({
      om: removeModeAt(d.om, i),
      params: dropModeFromParams(d.params, d.om.modes[i]),
    }));

  const commitAdd = () => {
    const name = newMode.trim();
    if (!name) return;
    setDraft((d) => ({ ...d, om: addMode(d.om, name) }));
    setNewMode("");
  };
  const save = () => {
    if (errors.length > 0) return;
    onSave({ ...entity, operating_modes: draft.om, parameters: draft.params });
    onDone();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <CardTitle className="flex flex-1 items-center gap-2 text-sm">
          <Workflow className="size-4" /> Operating modes
          <Badge variant="warning" className="px-1.5 py-0 text-[11px]">
            editing
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-on-surface-muted">Modes</span>
          {draft.om.modes.map((m, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={m}
                aria-label={`Mode ${i + 1} name`}
                className="h-8 font-mono text-xs"
                onChange={(e) => renameMode(i, e.target.value)}
              />
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Remove mode ${m}`}
                onClick={() => removeMode(i)}
              >
                <X className="size-3.5" />
              </Button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Input
              value={newMode}
              placeholder="Add a mode…"
              aria-label="New mode name"
              className="h-8 font-mono text-xs"
              onChange={(e) => setNewMode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitAdd();
                }
              }}
            />
            <Button size="sm" variant="outline" onClick={commitAdd} disabled={!newMode.trim()}>
              <Plus className="size-3.5" /> Add
            </Button>
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-on-surface-muted">
          Current mode
          <select
            value={draft.om.current_mode ?? ""}
            aria-label="Current mode"
            className={SELECT_CLASS}
            onChange={(e) =>
              setDraft((d) => ({ ...d, om: setCurrentMode(d.om, e.target.value || undefined) }))
            }
          >
            <option value="">(none)</option>
            {draft.om.modes.map((m, i) => (
              <option key={i} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-on-surface-muted">Transitions</span>
            <Button
              size="sm"
              variant="outline"
              className="ml-auto"
              onClick={() => setDraft((d) => ({ ...d, om: addTransition(d.om) }))}
              disabled={draft.om.modes.length === 0}
            >
              <Plus className="size-3.5" /> Add transition
            </Button>
          </div>
          {draft.om.transitions.map((_, i) => (
            <TransitionEditorRow
              key={i}
              om={draft.om}
              index={i}
              onChange={(nextOm) => setDraft((d) => ({ ...d, om: nextOm }))}
            />
          ))}
          {draft.om.transitions.length === 0 && (
            <p className="text-[11px] text-on-surface-muted">No transitions.</p>
          )}
        </div>

        {draft.params.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-on-surface-muted">
              Parameter activation
            </span>
            <p className="text-[11px] text-on-surface-muted">
              Which modes each parameter is measured in. None selected = active in
              every mode.
            </p>
            {draft.params.map((p, i) => (
              <ParamActivationRow
                key={p.name}
                param={p}
                index={i}
                modes={draft.om.modes}
                onToggle={(idx, mode) =>
                  setDraft((d) => ({ ...d, params: toggleParamModeAt(d.params, idx, mode) }))
                }
              />
            ))}
          </div>
        )}

        {errors.length > 0 && (
          <ul className="flex flex-col gap-0.5 rounded-md border border-danger/40 bg-danger/5 px-2.5 py-1.5 text-[11px] text-danger">
            {errors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={errors.length > 0}>
            <Save className="size-3.5" /> Save
          </Button>
          <Button size="sm" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
